/* eslint-disable @typescript-eslint/no-unused-vars */
import type { ExecutiveSummaryAnswer } from '@app/@types/redux/answer';
import type { ExecutiveSummaryOptions } from '@app/@types/redux/pageElement';
import { i18nCText } from '@libs/i18n/I18n';
import logger from '@libs/log';
import { translate } from '@state/utils/helper';
import classNames from 'classnames';
import type Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react';

const EditorCanvas = ({
  question,
  index,
  answers,
  preview,
  handleAnswerChange,
  isEssay,
}: {
  question?: {
    id: string;
    question: string;
    title?: string;
    maxWordCount?: string;
  };
  index: number;
  answers: ExecutiveSummaryAnswer | undefined;
  preview?: boolean;
  handleAnswerChange?: (index: string, question: string, answer: string) => void;
  isEssay?: boolean;
}) => {
  // TODO: Remove this once we have a way to check word limit for executive summary and essay
  // const wordLimit: number = question?.maxWordCount ? parseInt(question.maxWordCount) * 5 : 1000;
  // const [limitExceeded, setLimitExceeded] = useState(false);
  const [focus, setFocus] = useState(false);
  const questionId = question?.id ?? '';
  const questionText = (!isEssay ? question?.title : question?.question) ?? '';
  const editorRef = useRef<Quill | null>(null);

  const value =
    Object.keys(answers ?? {}).length && answers?.[questionId] ? answers[questionId].answer : '';

  const handleTextChange = () => {
    if (preview) return;
    if (editorRef.current) {
      const content = editorRef.current.root.innerHTML;
      // const length = content.replace(/\s+/g, '').length;

      // if (length >= wordLimit) {
      //   setLimitExceeded(true);
      // } else {
      //   setLimitExceeded(false);
      // }

      handleAnswerChange?.(questionId, questionText, content);
    }
  };

  return (
    <div
      className={classNames('summaryBlock', {
        'summaryBlock--preview': preview,
        'summaryBlock--focus': focus,
      })}
    >
      {!isEssay && <h2 className="summaryBlock__question">{questionText}</h2>}
      <div
        className={classNames('summaryBlock__answer', 'editor', {
          'editor--focus': focus,
          'summaryBlock__answer--essay': isEssay,
        })}
      >
        <Editor
          key={`ExecutiveSummary-index-${index.toString()}`}
          ref={editorRef}
          readOnly={preview}
          defaultValue={value}
          onTextChange={handleTextChange}
          onFocusIn={() => {
            setFocus(true);
          }}
          onFocusOut={() => {
            setFocus(false);
          }}
        />
        {/* {limitExceeded ? (
          <span className="summaryBlock__error">
            {translate('pageElements.executiveSummary.limitExceeded')}
          </span>
        ) : null} */}
      </div>
    </div>
  );
};

const ExecutiveSummaryView = (props: {
  answers: ExecutiveSummaryAnswer | undefined;
  handleAnswerChange?: (id: string, question: string, answer: string) => void;
  options: ExecutiveSummaryOptions;
  preview?: boolean;
}) => {
  const { answers, handleAnswerChange, options, preview } = props;
  const isEssay = options.canvasType === 'essay_canvas_creator';
  const questions = !isEssay ? options.questions : options.questions.slice(0, 1);
  return (
    <div className="exs-container">
      <div className="headingBlock">
        <div className="w-100">
          <h1 className="title">
            {options.stageName
              ? i18nCText(options.stageName)
              : options.title
              ? i18nCText(options.title)
              : options.questions.length === 1
              ? translate('pageElements.executiveSummary.essaySubmission')
              : translate('pageElements.executiveSummary.executiveSummary')}
          </h1>
          <div className="d-flex justify-content-between w-100">
            <span />
            {/* <p className="subtitle">{options.subtitle ?? ''}</p> */}
            <time className="year">{new Date().getFullYear()}</time>
          </div>
        </div>
      </div>
      {questions.map((question, index) => (
        <EditorCanvas
          key={question.id}
          question={question}
          index={index}
          answers={answers}
          preview={preview}
          handleAnswerChange={handleAnswerChange}
          isEssay={isEssay}
        />
      ))}
    </div>
  );
};

export default ExecutiveSummaryView;

// Editor is an uncontrolled React component
interface EditorProps {
  readOnly?: boolean;
  defaultValue?: string;
  onTextChange?: (delta: unknown, oldContents: unknown, source: string) => void;
  onSelectionChange?: (
    range: { index: number; length: number } | null,
    oldRange: { index: number; length: number } | null,
    source: string
  ) => void;
  onFocusIn?: () => void;
  onFocusOut?: () => void;
}

const Editor = forwardRef<Quill, EditorProps>(
  ({ readOnly, defaultValue, onTextChange, onSelectionChange, onFocusIn, onFocusOut }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const defaultValueRef = useRef(defaultValue);
    const onTextChangeRef = useRef(onTextChange);
    const onSelectionChangeRef = useRef(onSelectionChange);
    const onFocusInRef = useRef(onFocusIn);
    const onFocusOutRef = useRef(onFocusOut);
    const quillInstanceRef = useRef<Quill | null>(null);
    const isToolbarClickedRef = useRef(false);

    useLayoutEffect(() => {
      onTextChangeRef.current = onTextChange;
      onSelectionChangeRef.current = onSelectionChange;
      onFocusInRef.current = onFocusIn;
      onFocusOutRef.current = onFocusOut;
    });

    useEffect(() => {
      if (ref && 'current' in ref && ref.current) {
        ref.current.enable(!readOnly);
      }
    }, [ref, readOnly]);

    // Function to toggle toolbar visibility
    const toggleToolbarVisibility = (show: boolean) => {
      if (containerRef.current) {
        const toolbarElement = containerRef.current.querySelector('.ql-toolbar');
        if (toolbarElement) {
          (toolbarElement as HTMLElement).style.display = show ? 'block' : 'none';
        }
      }
    };

    useEffect(() => {
      // Import Quill dynamically to avoid SSR issues
      import('quill')
        .then((QuillModule) => {
          const Quill = QuillModule.default;

          if (!containerRef.current) return;

          const container = containerRef.current;
          // Clear any existing content first
          container.innerHTML = '';
          const editorContainer = container.appendChild(
            container.ownerDocument.createElement('div')
          );

          // Add custom class to the container for specific styling
          container.classList.add('executive-summary-editor');

          // Configure Quill with similar toolbar options to TinyMCE, but without color options
          const quill = new Quill(editorContainer, {
            theme: 'snow',
            modules: {
              toolbar: [
                ['bold', 'italic', 'underline'],
                [{ size: [] }],
                [{ align: [] }],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['clean'],
              ],
            },
            placeholder: 'Type your answer here...',
            readOnly: readOnly,
            formats: ['bold', 'italic', 'underline', 'size', 'align', 'list'], // Explicitly specify allowed formats
          });

          // Set default font
          quill.root.style.fontFamily =
            "'Helvetica Now Display', 'Helvetica Neue', Helvetica, Arial, sans-serif";
          quill.root.style.fontWeight = '300';

          quillInstanceRef.current = quill;

          if (ref && 'current' in ref) {
            ref.current = quill;
          }

          if (defaultValueRef.current) {
            quill.clipboard.dangerouslyPasteHTML(defaultValueRef.current);
          }

          quill.on('text-change', (delta, oldContents, source) => {
            onTextChangeRef.current?.(delta, oldContents, source);
          });

          quill.on('selection-change', (range, oldRange, source) => {
            onSelectionChangeRef.current?.(range, oldRange, source);
          });

          // Hide toolbar initially
          toggleToolbarVisibility(false);

          // Add event listeners to the toolbar to prevent it from hiding when clicked
          const toolbarElement = container.querySelector('.ql-toolbar');
          if (toolbarElement) {
            toolbarElement.addEventListener('mousedown', (e) => {
              isToolbarClickedRef.current = true;
              e.preventDefault(); // Prevent default to avoid losing focus
            });

            toolbarElement.addEventListener('mouseup', () => {
              // Reset after a short delay to allow the editor to regain focus
              setTimeout(() => {
                isToolbarClickedRef.current = false;
              }, 100);
            });
          }

          // Handle focus events
          editorContainer.addEventListener(
            'focus',
            () => {
              toggleToolbarVisibility(true);
              onFocusInRef.current?.();
            },
            true
          );

          editorContainer.addEventListener(
            'blur',
            (e) => {
              // Only hide toolbar if we're not clicking on the toolbar itself
              if (!isToolbarClickedRef.current) {
                // Check if the related target is within the toolbar
                const toolbarElement = container.querySelector('.ql-toolbar');
                if (toolbarElement && !toolbarElement.contains(e.relatedTarget as Node)) {
                  toggleToolbarVisibility(false);
                  onFocusOutRef.current?.();
                }
              }
            },
            true
          );

          return () => {
            if (ref && 'current' in ref) {
              ref.current = null;
            }

            // Remove event listeners if container still exists
            if (containerRef.current) {
              const toolbarElement = containerRef.current.querySelector('.ql-toolbar');
              if (toolbarElement) {
                // Clean up all event listeners
                toolbarElement.replaceWith(toolbarElement.cloneNode(true));
              }

              const editorContainer = containerRef.current.querySelector('.ql-container');
              if (editorContainer) {
                editorContainer.replaceWith(editorContainer.cloneNode(true));
              }
            }

            container.innerHTML = '';
          };
        })
        .catch((error: unknown) => {
          logger.error(error);
        });
    }, [ref]);

    return <div ref={containerRef} className="executive-summary-editor"></div>;
  }
);

Editor.displayName = 'Editor';
